/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { Column } from "../../../../common/commonComponents/lotDetailsTable";
import { PAContainerCleaning } from "../mediaCleaning.types";

export const getPAMediaCleaningColumns = (
	t: any,
	handleStatusChange: (row: PAContainerCleaning, status: string) => void,
	handleRemarksChange: (row: PAContainerCleaning, remarks: string) => void,
	errors: {
		cleaningAgent: string;
		containers: Record<string, string>;
	},
): Column[] => [
	{
		header: t("postAutoclaveProcess.mediaCleaning.columns.plateId"),
		key: "plateId",
		render: (row: PAContainerCleaning) => (
			<>
				<div className="flex items-center gap-3">
					<div
						className={`w-2 h-2 rounded-full ${row.visual_cleanliness === "Pass" ? "bg-emerald-500" : row.visual_cleanliness === "Fail" ? "bg-red-500" : "bg-slate-300"}`}
					></div>
					<span className="text-sm font-black text-slate-700 tracking-tight">
						{row.container_serial_number}
					</span>
				</div>
			</>
		),
	},
	{
		header: t("postAutoclaveProcess.mediaCleaning.columns.retryCount"),
		key: "plateId",
		render: (row: PAContainerCleaning) => (
			<>
				<span
					className={`px-3 py-1 rounded-full text-[11px] font-black ${Number(row.current_attempt) > 0 ? "bg-orange-100 text-orange-600 border border-orange-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}
				>
					{row.current_attempt} / {row.max_attempts}
				</span>
			</>
		),
	},
	{
		header: t("postAutoclaveProcess.mediaCleaning.columns.visualCleanliness"),
		key: "plateId",
		render: (row: PAContainerCleaning) => (
			<>
				<div className="flex items-center gap-6">
					<label className="flex items-center gap-2.5 cursor-pointer group">
						<div className="relative flex items-center justify-center">
							<input
								type="radio"
								name={`status-${row.container_id}`}
								checked={row.visual_cleanliness === "Pass"}
								onChange={() => handleStatusChange(row, "Pass")}
								className="peer sr-only"
							/>
							<div className="w-5 h-5 border-2 border-slate-200 rounded-full peer-checked:border-emerald-500 peer-checked:bg-emerald-500 transition-all"></div>
							<span className="material-symbols-outlined text-white text-xs absolute opacity-0 peer-checked:opacity-100 transition-opacity">
								done
							</span>
						</div>
						<span
							className={`text-[11px] font-black uppercase tracking-widest transition-colors ${row.visual_cleanliness === "Pass" ? "text-emerald-600" : "text-slate-400 group-hover:text-slate-600"}`}
						>
							Pass
						</span>
					</label>
					<label className="flex items-center gap-2.5 cursor-pointer group">
						<div className="relative flex items-center justify-center">
							<input
								type="radio"
								name={`status-${row.container_id}`}
								checked={row.visual_cleanliness === "Fail"}
								onChange={() => handleStatusChange(row, "Fail")}
								className="peer sr-only"
							/>
							<div className="w-5 h-5 border-2 border-slate-200 rounded-full peer-checked:border-red-500 peer-checked:bg-red-500 transition-all"></div>
							<span className="material-symbols-outlined text-white text-xs absolute opacity-0 peer-checked:opacity-100 transition-opacity">
								close
							</span>
						</div>
						<span
							className={`text-[11px] font-black uppercase tracking-widest transition-colors ${row.visual_cleanliness === "Fail" ? "text-red-600" : "text-slate-400 group-hover:text-slate-600"}`}
						>
							Fail
						</span>
					</label>
				</div>
			</>
		),
	},
	{
		header: t("postAutoclaveProcess.mediaCleaning.columns.remarks"),
		key: "plateId",
		render: (row: PAContainerCleaning) => (
			<div className="relative max-w-md">
				<input
					type="text"
					placeholder={
						row.visual_cleanliness === "Fail"
							? "Enter reason for failure..."
							: "Optional remarks..."
					}
					value={row.remarks ?? ""} 
					onChange={(e) => handleRemarksChange(row, e.target.value)}
					className={`w-full px-4 py-2.5 bg-white border rounded-xl text-xs font-medium outline-none transition-all ${
						errors.containers[row.container_id]
							? "border-red-300 ring-2 ring-red-50 focus:border-red-500"
							: "border-slate-200 focus:border-[#FF7043] focus:ring-2 focus:ring-orange-50"
					}`}
				/>
				{errors.containers[row.container_id] && (
					<p className="text-[11px] text-red-500 mt-1">
						{errors.containers[row.container_id]}
					</p>
				)}
			</div>
		),
	},
];
